import { COMING_SOON_STATES, STATE_TITLE_KEY_MAP } from '../../constants';
import useTranslation from '../useTranslation';
import downloadBlob from 'utils/downloadBlob';

import useMixApplyCalcs from 'hooks/useMixApplyCalcs';

import type { PdfLabels, Props, TankVolumeSummary, UseDataReturn } from './types';

const buildPdfLabels = (translations: ReturnType<typeof useTranslation>): PdfLabels => ({
    acresPerTank: translations.ACRES_PER_TANK,
    applicationDetails: translations.APPLICATION_DETAILS,
    applicationTankGal: translations.APPLICATION_TANK_GAL,
    applicator: translations.APPLICATOR,
    batches: translations.BATCHES,
    carrierRateGalPerAc: translations.CARRIER_RATE_GAL_PER_AC,
    carrierType: translations.CARRIER_TYPE,
    county: translations.COUNTY,
    crop: translations.CROP,
    date: translations.DATE_PDF,
    epaRegNumber: translations.EPA_COLUMN,
    faaNNumber: translations.FAA_N_NUMBER,
    fieldName: translations.FIELD_NAME,
    gustSpeed: translations.GUST_SPEED,
    humidity: translations.HUMIDITY,
    license: translations.LICENSE,
    licenseState: translations.LICENSE_STATE,
    method: translations.METHOD_LABEL,
    mixTankGal: translations.MIX_TANK_GAL,
    name: translations.NAME_LABEL,
    order: translations.ORDER_COLUMN,
    product: translations.PRODUCT_COLUMN,
    rate: translations.RATE_COLUMN,
    recordedAt: translations.RECORDED_AT,
    signature: translations.SIGNATURE,
    signatureDate: translations.SIGNATURE_DATE,
    signedBy: translations.SIGNED_BY,
    state: translations.STATE,
    tankMix: translations.TANK_MIX,
    tankVolume: translations.TANK_VOLUME,
    targetPest: translations.TARGET_PEST,
    temperature: translations.TEMPERATURE,
    totalAcres: translations.TOTAL_ACRES,
    totalApplied: translations.TOTAL_APPLIED,
    totalSprayVolumeGal: translations.TOTAL_SPRAY_VOLUME_GAL,
    type: translations.TYPE_LABEL,
    weatherAtApplication: translations.WEATHER_AT_APPLICATION,
    weatherUnavailable: translations.WEATHER_UNAVAILABLE,
    windDirection: translations.WIND_DIRECTION,
    windSpeed: translations.WIND_SPEED
});

const useData = ({ application, mixRecipe, tankState, weather }: Props): UseDataReturn => {
    const translations = useTranslation();

    const calcs = useMixApplyCalcs({
        appTankGal: tankState.appTankGal,
        carrierRateGalPerAc: tankState.carrierRateGalPerAc,
        mixTankGal: tankState.mixTankGal,
        products: mixRecipe.products.map(product => ({
            densityLbPerGal: product.densityLbPerGal ?? null,
            productId: product.productId,
            productName: product.productName,
            rate: product.rate,
            rateUnit: product.rateUnit
        })),
        totalAcres: application.totalAcres
    });

    const tankVolumeSummary: TankVolumeSummary = {
        acresPerTank: calcs.acresPerTank,
        appTankGal: tankState.appTankGal,
        batches: calcs.batches,
        carrierRateGalPerAc: tankState.carrierRateGalPerAc,
        carrierType: tankState.carrierType,
        mixTankGal: tankState.mixTankGal,
        totalSprayVolumeGal: calcs.totalSprayVolumeGal
    };

    const stateTitleKey = STATE_TITLE_KEY_MAP[application.state];
    const stateTitle = stateTitleKey ? translations[stateTitleKey] : translations.FALLBACK_TITLE;

    const hasUnlicensedProduct = mixRecipe.products.some(product =>
        (product.unlicensedStates ?? []).includes(application.state)
    );

    const isComingSoonState = (COMING_SOON_STATES as ReadonlyArray<string>).includes(
        application.state
    );

    const handleDownloadPdf = async (): Promise<void> => {
        if (hasUnlicensedProduct) {
            return;
        }
        const { pdf } = await import('@react-pdf/renderer');
        const { default: StateComplianceReportPdf } =
            await import('../../components/StateComplianceReportPdf');
        const labels = buildPdfLabels(translations);
        const blob = await pdf(
            <StateComplianceReportPdf
                application={application}
                labels={labels}
                mixRecipe={mixRecipe}
                tankVolumeSummary={tankVolumeSummary}
                title={stateTitle}
                weather={weather}
            />
        ).toBlob();

        downloadBlob({ blob, filename: `${application.id}-${application.state}-report.pdf` });
    };

    return {
        handleDownloadPdf,
        hasUnlicensedProduct,
        isComingSoonState,
        stateTitle,
        tankVolumeSummary
    };
};

export default useData;
